import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright   : 2020 semantics GmbH
//  Date        : $Date: 2026-05-27 15:23:24 +0200 (Wed, 27 May 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 163322 $
//  Purpose     : hooks for fulltext package
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import log from 'uix/logging';
import { Actions, Sel } from 'uix/registries';
import { useSelector, useActions, useEffect, usePersistentShallow, useMemo, useApi, useCallback, useState, useDebounce } from 'uix/hooks';
import { dict, groupBy, mapItems, size, values, strip, filterItems, sum, items, isNil, sorted, isObject } from 'ldash';
import { isSSR } from 'uix/utils';
// ---Local imports
import { HYPHEN, DELETED_WORD } from './constants';
import styles from './styles.css';
// ---Globals
export const useOcrConfig = ()=>{
    let ocr_cfg = useSelector((root)=>root.config.ocr);
    let loadCfgOcr = useActions('loadCfgOcr');
    useEffect(()=>{
        if (!ocr_cfg && ocr_cfg !== 'LOADING') {
            loadCfgOcr();
        }
    }, []);
    return ocr_cfg === 'LOADING' ? null : ocr_cfg;
};
// note: update the version number if you add new values here
export const INITIAL_OPTIONS = {
    infoActive: false,
    splitVertical: true,
    // text line rendering
    fontSize: 14,
    wrapLines: true,
    // word editor
    evalCharmodel: false,
    autoAlignLines: false,
    speechMode: false,
    // viewer related
    showViewer: true,
    // compare view
    compareMode: 'lines',
    diffNormalize: false,
    diffNormalizeBow: false,
    diffSideBySide: true,
    // test result table
    col_w: true,
    col_l: false,
    col_p: false,
    col_b: false,
    col_cer: false,
    col_wer: true,
    col_pz5: true,
    // position Aware Words
    pawCrop: true,
    pawOutlines: {
        words: false,
        lines: false
    }
};
export const INITIAL_VIEWER_OPTIONS = {
    geometry: {
        words: true,
        lines: false,
        paragraphs: false,
        blocks: false,
        imgboxes: false
    },
    showGeometryOrder: false,
    showTextEditor: false,
    zoomToWidth: true,
    // viewer geometry edit
    mouseSelectGeometry: false,
    mouseInsertGeometry: false,
    // allow move via mouse
    mouseMoveGeometry: true,
    geometryMode: 'line'
};
export const usePersistentOcrViewerOptions = ()=>{
    let [opts, setOpts] = usePersistentShallow('OcrViewerOptions', INITIAL_VIEWER_OPTIONS, 3);
    let request = useSelector(Sel.request);
    // change to transcriber user?
    let simpleMenuMode = request.classname == 'xtranscript';
    let _opts = useMemo(()=>simpleMenuMode ? {
            ...opts,
            geometryMode: 'line',
            simpleMenuMode: true,
            mouseMoveGeometry: false
        } : opts, [
        simpleMenuMode,
        opts
    ]);
    return [
        _opts,
        setOpts
    ];
};
export const usePersistentOptions = ()=>usePersistentShallow('FulltextAdminOptions', INITIAL_OPTIONS, 18);
export const isCoordlessBbox = (bbox)=>bbox?.length === 4 && bbox[0] === 0 && bbox[1] === 0 && bbox[2] === 0 && bbox[3] === 0;
export const hasGeometryBbox = (bbox)=>bbox?.length === 4 && !isCoordlessBbox(bbox);
export const hasObjGeometryBbox = (bbox)=>Boolean(isObject(bbox) && 'x1' in bbox && 'y1' in bbox && 'x2' in bbox && 'y2' in bbox);
export const getLineCoords = (words)=>{
    /* derive line(s) bbox + line-text from words of line
   */ let line2words = groupBy(words, (w)=>w.l_idx);
    let lines = sorted(mapItems(line2words, (index, line)=>{
        if (line.length == 0) {
            return null;
        }
        let filtered_line = line.filter((w)=>w.text !== DELETED_WORD);
        if (filtered_line.length == 0) {
            // keep at least initial word
            filtered_line = [
                line[0]
            ];
        }
        let geom_words = filtered_line.filter((w)=>hasGeometryBbox(w.bbox));
        let isRtlInversion = geom_words.length > 1 && geom_words[0].bbox[0] > geom_words.at(-1).bbox[0];
        let first_w = geom_words.at(isRtlInversion ? -1 : 0);
        let last_w = geom_words.at(isRtlInversion ? 0 : -1);
        return [
            parseInt(index),
            {
                bbox: first_w && last_w ? {
                    x1: first_w.bbox[0],
                    y1: Math.min(first_w.bbox[1], last_w.bbox[1]),
                    x2: last_w.bbox[2],
                    y2: Math.max(first_w.bbox[3], last_w.bbox[3])
                } : null,
                coordless: !(first_w && last_w),
                text: filtered_line.map((w)=>w.text).join('')
            }
        ];
    }), (e)=>e[0]).map((e)=>e[1]);
    return lines;
};
export const getLines = (words)=>{
    /* return lines with enriched words
   */ let line2words = groupBy(words, (w)=>w.l_idx);
    let lines = values(line2words).map((line)=>line.map((w)=>({
                ...w,
                index: w.w_idx
            })));
    let avg_char_widths = [];
    // resolve dehyphenized words into text_unhyp
    lines = lines.map((l, i)=>{
        let next_line = lines[i + 1];
        let prev_line = lines[i - 1];
        return l.map(({ text, bbox, ...rest }, j)=>{
            let w = bbox[2] - bbox[0];
            if (text.length && w > 0) {
                avg_char_widths.push(w / text.length);
            }
            let normalized = strip(text);
            if (normalized.endsWith(HYPHEN)) {
                if (next_line?.[0]) {
                    normalized = normalized.slice(0, -1) + strip(next_line[0].text);
                } else {
                    normalized = normalized.slice(0, -1);
                }
            } else if (j == 0 && prev_line && prev_line.at(-1).text.endsWith(HYPHEN)) {
                normalized = strip(prev_line.at(-1).text).toLowerCase().slice(0, -1) + normalized;
            }
            return {
                ...rest,
                bbox,
                text,
                text_unhyp: normalized
            };
        });
    });
    let avg_char_width = avg_char_widths.length ? sum(avg_char_widths) / avg_char_widths.length : 0;
    return {
        lines,
        avg_char_width
    };
};
export const useNormalizedFulltextInfo = (fulltextInfo)=>/* transform array structure into dict structure
   */ useMemo(()=>{
        if (!fulltextInfo?.wordInfo) return {};
        let p = fulltextInfo;
        let words = p.wordInfo.words.map(([x1, y1, x2, y2, text, l_idx, readonly], w_idx)=>({
                bbox: [
                    x1,
                    y1,
                    x2,
                    y2
                ],
                text,
                w_idx,
                l_idx,
                readonly,
                coordless: isCoordlessBbox([
                    x1,
                    y1,
                    x2,
                    y2
                ])
            }));
        return {
            ...p,
            wordInfo: {
                ...p.wordInfo,
                words
            }
        };
    }, [
        fulltextInfo
    ]);
export const useNormalizedFulltextApi = ({ otid, enabled })=>{
    let { data, loading, reload } = useApi('getFulltextInfo', {
        otid: otid
    }, {
        condition: enabled,
        autoreset: false
    });
    let fulltextInfo = useNormalizedFulltextInfo(data?.fulltextInfo);
    return {
        fulltextInfo,
        loading,
        reload
    };
};
const EMPTY_PAGEDATA = {
    pages: {}
};
export const usePageData = (fulltextInfo)=>useMemo(()=>{
        if (!fulltextInfo) {
            return EMPTY_PAGEDATA;
        }
        let pages = fulltextInfo.pages || [
            fulltextInfo
        ];
        pages = pages.filter((p)=>p.wordInfo);
        let otids = pages.map((p)=>p.otid);
        pages = dict(pages.map((p, order)=>{
            let words = p.wordInfo.words;
            let { lines, avg_char_width } = getLines(words);
            let lineCoords = getLineCoords(words);
            // resolve word text for annotation, easier for consumers (EntityLinkList)
            let annotations = (p.annotations || []).map((anno)=>({
                    ...anno,
                    text: anno.words.map((w_idx)=>words[w_idx].text).join(' ')
                }));
            let wordInfo = {
                ...p.wordInfo,
                words
            };
            return [
                p.otid,
                {
                    ...p,
                    lines,
                    avg_char_width,
                    order,
                    lineCoords,
                    wordInfo,
                    annotations
                }
            ];
        }));
        return {
            pages
        };
    }, [
        fulltextInfo
    ]);
export const useFirstPageData = (fulltextInfo)=>{
    let { pages } = usePageData(fulltextInfo);
    return useMemo(()=>values(pages)[0] ?? {
            wordInfo: {
                words: []
            },
            lines: [],
            annotations: []
        }, [
        pages
    ]);
};
export const useCharmodelAspect = ({ otid, test_name, enabled, deps, colorizer, master_mode })=>{
    let params = dict(filterItems({
        otid,
        test_name,
        master_mode
    }, (k, v)=>v));
    let result = useApi('getCharModelDetails', params, {
        condition: enabled,
        cached: true,
        deps
    });
    return useMemo(()=>{
        let language = result.data?.language;
        let get_conf_100 = (text_unhyp)=>result.data?.word2conf_100[text_unhyp.toLowerCase()];
        let in_vocab = (conf_100)=>conf_100 === 100;
        let getClassname = ({ text_unhyp })=>{
            if (result.loading || !result.data) return null;
            let conf_100 = get_conf_100(text_unhyp);
            let name = null;
            if (!conf_100) {
                name = 'ignore';
            } else if (conf_100 < 40) {
                name = 'worst';
            } else if (conf_100 < 50) {
                name = 'worse';
            } else if (conf_100 < 60) {
                name = 'bad';
            } else if (conf_100 < 100) {
                name = 'ok';
            } else if (in_vocab(conf_100)) {
                // in-vocab
                name = 'invocab';
            } else if (conf_100 > 100) {
                name = 'good';
            }
            if (colorizer == 'red' && ![
                'worst',
                'worse',
                'bad'
            ].includes(name)) return null;
            return styles[name] || null;
        };
        let getTitle = ({ text_unhyp })=>{
            let conf_100 = get_conf_100(text_unhyp);
            return conf_100 && !in_vocab(conf_100) ? `${text_unhyp}: ${conf_100.toFixed(0)}` : null;
        };
        let renderWord = ({ text, text_unhyp })=>{
            // given a word from above getLines()
            if (!result.loading && result.data) {
                let conf_100 = get_conf_100(text_unhyp);
                return /*#__PURE__*/ _jsx("span", {
                    className: getClassname({
                        text_unhyp
                    }),
                    title: getTitle({
                        text_unhyp
                    }),
                    children: text
                });
            }
            return text;
        };
        return {
            renderWord,
            getClassname,
            getTitle,
            language
        };
    }, [
        result
    ]);
};
export const useViewerMovements = ({ scrollToMode = 'jump', align = 'top', keepZoom = true })=>{
    /* control a position with toRect()
   */ // scrollToMode: jump, zoom, native, scroll
    let [position, setPosition] = useState(null);
    let toRect = (id, x1, y1, x2, y2)=>setPosition({
            id,
            x1: x1 - 20,
            y1: y1 - 5,
            x2: x2 + 20,
            y2,
            align,
            scrollToMode,
            keepZoom
        });
    return {
        position,
        toRect
    };
};
export const useViewerMovementsWords = ({ pages, scrollToMode = 'jump', align = 'top' })=>{
    /* control a position with toLine(), toWord(), toRect() for given pageData
   */ let { position, toRect } = useViewerMovements({
        scrollToMode,
        align
    });
    let toLine = useCallback((otid, line_index)=>{
        let bbox = pages[otid].lineCoords[line_index]?.bbox;
        if (!bbox) {
            return;
        }
        let { x1, y1, x2, y2 } = bbox;
        let h = y2 - y1;
        toRect(otid, x1, y1 - h, x2, y2 + h);
    }, [
        pages
    ]);
    let toWord = useCallback((otid, word_index, enlarge_to_chars = 20)=>{
        let page = pages[otid];
        let word = page?.wordInfo.words[word_index];
        if (word) {
            let { bbox, text } = word;
            if (!hasGeometryBbox(bbox)) {
                return;
            }
            let [x1, y1, x2, y2] = bbox;
            let h = y2 - y1;
            if (!text.length || enlarge_to_chars == 0) {
                toRect(otid, x1, y1 - h, x2, y2 + h, 'center');
            } else {
                let N = Math.max(enlarge_to_chars, text.length);
                let w = x2 - x1;
                let w_N = page.avg_char_width * N;
                let more_w_half = Math.ceil(w_N - w / 2);
                toRect(otid, x1 - more_w_half, y1 - h, x2 + more_w_half, y2 + h, 'center');
            }
        }
    }, [
        pages
    ]);
    return useMemo(()=>({
            position,
            toLine,
            toWord,
            toRect,
            scrollToMode
        }), [
        position,
        toLine,
        toWord,
        toRect,
        scrollToMode
    ]);
};
export const useTextLineScrolling = (vid, pages)=>{
    // geometry selection: from single mouse click select in geometry
    let page2geom = useSelector((root)=>root.data.viewer.selectedGeometry[vid]);
    let [page_otid, line_idx] = useMemo(()=>{
        if (size(page2geom)) {
            let line_idx = null;
            let [page_otid, selected_geom] = items(page2geom)[0];
            let page = pages[page_otid];
            if (page) {
                // first try selected words
                let first_selected_word = selected_geom.words?.[0];
                if (first_selected_word) {
                    let word = page.wordInfo.words[first_selected_word];
                    if (word) {
                        line_idx = word.l_idx;
                    }
                } else if (size(selected_geom.lines)) {
                    // fallback to lines
                    line_idx = selected_geom.lines[0];
                }
            }
            return [
                page_otid,
                line_idx
            ];
        }
        return [
            null,
            null
        ];
    }, [
        page2geom
    ]);
    let buildLineScrollId = useCallback((otid, line_idx)=>`line_${otid}_${line_idx}`, []);
    useEffect(()=>{
        if (!isNil(line_idx) && !isSSR) {
            let el = document.getElementById(buildLineScrollId(page_otid, line_idx));
            if (!el) {
                log.warn('line target not found: %o', line_idx);
            }
            el?.scrollIntoView({
                block: 'center',
                behavior: 'smooth'
            });
        }
    }, [
        page_otid,
        line_idx
    ]);
    return {
        buildLineScrollId,
        selectedLineIdx: line_idx
    };
};
const EMPTY = {};
export const useFocusedGeometry = (vid)=>{
    let focused_geom = useSelector((root)=>root.data.viewer.focusedGeometry[vid] || EMPTY);
    // bidirectional selection update is pretty costly
    // slow down here
    return useDebounce(focused_geom, 500)[0];
};
